/* eslint-disable sort-imports */
/**
 * Everything the board draws, derived from the week's rows on each render.
 *
 * Nothing here is stored: a manual move is saved on the server the moment it
 * is made and the assignments are refetched, so the board only ever draws
 * what is actually planned. The memos are keyed so a change of day, view or
 * drafts recomputes only what depends on it.
 */
import { useCallback, useMemo } from "react";

import { LaborStandard } from "../../../../../services/labor-standards-api";
import { AvailabilityRow, RosterAssignment } from "../../../../../services/roster-assignments-api";
import { ShiftInstance } from "../../../../../services/shifts-api";
import { ManufacturingOrder } from "../../../production-plan/utils/calculations";
import { overlapsDay } from "../../../utils/time";
import { Clock, CoverMethod, FlexRequestRow } from "../types";
import { KnownWorker } from "../ui/standing-crew-panel";
import { indexAvailability, outDaysLabel } from "../utils/availability";
import {
	BoardBlock,
	buildDayLoads,
	buildMetrics,
	buildWeekLoads,
	dayShiftCap,
	hourWindow,
	weekDayCaps,
	weekShiftCap,
} from "../utils/derive";
import { buildGapBoard, GapRow, SHIFT_LABEL, ShiftNumber, summariseGaps } from "../utils/gap-derive";
import { buildLaneRows, laneHourWindow } from "../utils/lane-rows";
import { DEFAULT_CAP_HOURS, fmtHours } from "../utils/tokens";
import { buildWeekLanes } from "../utils/week-lanes";

/** The most common value, so one odd shift does not move everyone's scale. */
export const typicalCap = (caps: number[], fallback: number): number => {
	if (!caps.length) {
		return fallback;
	}
	const counts = new Map<number, number>();
	caps.forEach((value) => counts.set(value, (counts.get(value) || 0) + 1));
	return Array.from(counts.entries()).sort((a, b) => b[1] - a[1] || b[0] - a[0])[0][0];
};

export interface BoardDerivationArgs {
	weekAssignments: RosterAssignment[];
	manufacturingOrders: ManufacturingOrder[];
	laborStandards: LaborStandard[];
	shifts: ShiftInstance[];
	/** Who is out (or asking to be) on the week's shifts. */
	availability: AvailabilityRow[];
	weekStartDate: Date;
	selectedDate: Date;
	operationNames?: Map<number, string>;
	workcenters: string[];
	requiredHours: number;
	isWeek: boolean;
	shift: ShiftNumber;
	drafts: Record<string, CoverMethod>;
	published: boolean;
	selected: number | null;
}

/** A gap row's "pull from" option: a qualified worker with slack, named by their own line. */
export type PullSource = (row: { jobRole: string }) => { workcenter: string; detail: string } | null;

export function useBoardDerivations({
	weekAssignments,
	manufacturingOrders,
	laborStandards,
	shifts,
	availability,
	weekStartDate,
	selectedDate,
	operationNames,
	workcenters,
	requiredHours,
	isWeek,
	shift,
	drafts,
	published,
	selected,
}: BoardDerivationArgs) {
	// Who is out, indexed once: every card, count and metric below asks it.
	const availabilityIndex = useMemo(() => indexAvailability(availability), [availability]);

	// Caps come from the shifts, per worker. The constant is only the fallback
	// when the week has no shift at all. The seven day resolvers are built
	// once here, not once per worker per cell.
	const dayCapResolver = useMemo(() => dayShiftCap(shifts, selectedDate), [shifts, selectedDate]);
	const weekCapResolver = useMemo(() => weekShiftCap(shifts, weekStartDate), [shifts, weekStartDate]);
	const weekDayCapResolvers = useMemo(() => weekDayCaps(shifts, weekStartDate), [shifts, weekStartDate]);

	const dayLoads = useMemo(
		() => buildDayLoads(weekAssignments, selectedDate, dayCapResolver, operationNames, availabilityIndex),
		[weekAssignments, selectedDate, dayCapResolver, operationNames, availabilityIndex]
	);
	const weekLoads = useMemo(
		() => buildWeekLoads(weekAssignments, weekStartDate, weekCapResolver, weekDayCapResolvers, availabilityIndex),
		[weekAssignments, weekStartDate, weekCapResolver, weekDayCapResolvers, availabilityIndex]
	);

	// The cap the scales and legend are drawn against: the roster's most common
	// per-worker cap (every normal day that is the plant's shift length).
	const cap = useMemo(() => typicalCap(dayLoads.map((load) => load.cap), DEFAULT_CAP_HOURS), [dayLoads]);
	const weekCap = useMemo(() => typicalCap(weekLoads.map((load) => load.cap), DEFAULT_CAP_HOURS * 5), [weekLoads]);

	const laneRows = useMemo(
		() =>
			buildLaneRows({
				day: selectedDate,
				workcenters,
				orders: manufacturingOrders,
				standards: laborStandards,
				assignments: weekAssignments,
				operationNames,
				shifts,
				availability: availabilityIndex,
			}),
		[selectedDate, workcenters, manufacturingOrders, laborStandards, weekAssignments, operationNames, shifts, availabilityIndex]
	);

	// Week · by line: seven days of lanes. The timeline draws both Week and Day from them.
	const weekLanes = useMemo(
		() =>
			buildWeekLanes(manufacturingOrders, weekAssignments, laborStandards, workcenters, weekStartDate, {
				operationNames,
				shifts,
				availability: availabilityIndex,
			}),
		[manufacturingOrders, weekAssignments, laborStandards, workcenters, weekStartDate, operationNames, shifts, availabilityIndex]
	);

	// Both day layouts share one clock so switching between them does not
	// slide the cards; the window is the union of what either would show.
	const clock = useMemo<Clock>(() => {
		const workers = hourWindow(dayLoads);
		const lanes = laneHourWindow(laneRows);
		return { start: Math.min(workers.start, lanes.start), end: Math.max(workers.end, lanes.end) };
	}, [dayLoads, laneRows]);
	const span = clock.end - clock.start;

	// Everyone on the week's roster, for the standing-crew form and the week ·
	// by line people list — each with the days they are out this week, if any.
	const knownWorkers = useMemo<KnownWorker[]>(() => {
		const seen = new Map<string, string>();
		weekAssignments.forEach((a) => {
			if (!seen.has(String(a.workerId))) {
				seen.set(String(a.workerId), a.workerName);
			}
		});
		return Array.from(seen.entries()).map(([id, name]) => {
			const rows = availabilityIndex.forWorker(id);
			return { id, name, outLabel: outDaysLabel(rows), unavailable: rows };
		});
	}, [weekAssignments, availabilityIndex]);

	// "On this day" is overlap, the same rule the hour lanes use — a step that
	// started yesterday and runs into today is on today's board.
	const dayOrders = useMemo(
		() => manufacturingOrders.filter((mo) => overlapsDay(mo.startTime, mo.endTime, selectedDate)),
		[manufacturingOrders, selectedDate]
	);

	const metrics = useMemo(
		() =>
			buildMetrics(
				isWeek ? weekLoads : dayLoads,
				isWeek ? weekCap : cap,
				dayOrders,
				isWeek ? requiredHours * 5 : requiredHours,
				fmtHours
			),
		[isWeek, weekLoads, dayLoads, weekCap, cap, dayOrders, requiredHours]
	);

	// Roles a worker has actually performed this week — the closest proxy the
	// client has to their qualifications without a second round-trip.
	const workerRoles = useMemo(() => {
		const map = new Map<string, Set<string>>();
		weekAssignments.forEach((a) => {
			const id = String(a.workerId);
			if (!map.has(id)) {
				map.set(id, new Set());
			}
			if (a.jobRoleName) {
				map.get(id)?.add(a.jobRoleName);
			}
		});
		return map;
	}, [weekAssignments]);

	const dayAssignments = useMemo(
		() => weekAssignments.filter((a) => overlapsDay(a.startTime, a.endTime, selectedDate)),
		[weekAssignments, selectedDate]
	);

	const gapRows = useMemo<GapRow[]>(
		() =>
			buildGapBoard({
				day: selectedDate,
				shift,
				orders: dayOrders,
				standards: laborStandards,
				assignments: dayAssignments,
				workerRoles,
				drafts,
				availability: availabilityIndex,
			}),
		[selectedDate, shift, dayOrders, laborStandards, dayAssignments, workerRoles, drafts, availabilityIndex]
	);
	const gapSummary = useMemo(() => summariseGaps(gapRows, drafts), [gapRows, drafts]);

	// Someone qualified for the row's role who still has slack under the cap.
	// Omitted entirely when nobody qualifies — never offer a move that cannot happen.
	const pullSource = useCallback<PullSource>(
		(row) => {
			const candidate = dayLoads.find(
				(load) => load.hours < load.cap - 1 && workerRoles.get(load.workerId)?.has(row.jobRole)
			);
			if (!candidate) {
				return null;
			}
			// The option has to name the worker's OWN line — "pull from Mechanical"
			// is a role, not somewhere you can pull a person from.
			const theirOrder = candidate.blocks[0]?.workOrderId;
			const theirCenter = dayOrders.find((order) => order.id === theirOrder)?.workstation || "another line";
			return {
				workcenter: theirCenter,
				detail: `${candidate.name} · ${fmtHours(candidate.hours)} today · ${fmtHours(candidate.cap - candidate.hours)} of slack`,
			};
		},
		[dayLoads, workerRoles, dayOrders]
	);

	const draftedRequests = useMemo<FlexRequestRow[]>(
		() =>
			Object.entries(drafts).map(([key, method]) => {
				const [workcenter, jobRole, blockRef] = key.split("::");
				const blockIndex = Number(blockRef.replace("b", ""));
				const row = gapRows.find((r) => r.workcenter === workcenter && r.jobRole === jobRole);
				const cell = row?.cells[blockIndex];
				// Block hours can run past 24 (3rd shift wraps), so roll the extra
				// hours into the next day rather than producing an invalid time.
				const toIso = (hour: number): string => {
					const date = new Date(selectedDate);
					date.setHours(0, 0, 0, 0);
					date.setDate(date.getDate() + Math.floor(hour / 24));
					date.setHours(hour % 24, 0, 0, 0);
					return date.toISOString();
				};
				return {
					key,
					workcenter,
					jobRole,
					window: `${cell?.block.label ?? ""} · ${SHIFT_LABEL[shift]}`,
					method,
					heads: cell?.headsShort ?? 1,
					status: published ? "OPEN" : "DRAFT",
					jobRoleId: row?.jobRoleId ?? null,
					startIso: toIso(cell?.block.start ?? 0),
					endIso: toIso(cell?.block.end ?? 0),
				};
			}),
		[drafts, gapRows, shift, published, selectedDate]
	);

	const selectedBlock = useMemo<BoardBlock | null>(() => {
		if (selected === null) {
			return null;
		}
		return dayLoads.flatMap((l) => l.blocks).find((b) => b.id === selected) || null;
	}, [selected, dayLoads]);

	return {
		availability,
		availabilityIndex,
		dayLoads,
		weekLoads,
		cap,
		weekCap,
		laneRows,
		weekLanes,
		clock,
		span,
		knownWorkers,
		dayOrders,
		metrics,
		gapRows,
		gapSummary,
		pullSource,
		draftedRequests,
		selectedBlock,
	};
}

export type BoardDerivations = ReturnType<typeof useBoardDerivations>;
